import { useIntl } from 'react-intl';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import clsx from 'clsx';

import { searchPermits } from 'api/algolia';
import { PermitsOverviewFieldsFragment } from 'api/graphql-types';
import { useAppSelector } from 'redux/store';
import { PermitAlgoliaFragment, setModalOpen } from 'redux/stores/permits';
import { IActiveFilter } from 'redux/stores/subsidy';

import { MobileActions } from 'modules/subsidy-overview/mobile-actions';

import { FilterSidebar } from 'components/filter-sidebar';
import { Card } from 'components/card/card';
import { Search } from 'components/search/search';
import { ISortOption } from 'components/sort';
import { useDebounce } from 'utils/use-debounce';
import { useWindowSize } from 'utils/use-window-size';
import { EmptyState } from 'components/empty-state/empty-state';

import { LAW_QUERY_KEY, LawArrayParam, PRODUCT_QUERY_KEY, ProductArrayParam, useLaw, useProduct } from './queries';
import { usePermitsFilters } from './data/use-permits-filters';

import styles from './permits-overview.module.scss';
import { CheckboxesModal } from 'components/checkboxes-modal/checkboxes-modal';
import { CheckboxItem } from 'components/core/checkbox-group';
import { useDispatch } from 'react-redux';
import {
	PAGE_QUERY_KEY,
	SEARCH_QUERY_KEY,
	SORT_QUERY_KEY,
	NewOldSortParam,
	THEME_QUERY_KEY,
	ThemeArrayParam,
	usePagination,
	useSearch,
	useSort,
	useTheme
} from 'utils/hooks/queries';

export const PERMIT_OVERVIEW_LIMIT = 15;

type FetchPermits = (
	page: number,
	newSort: ISortOption,
	newSearchValue: string | null,
	themeIds: string[],
	lawIds: string[],
	productType: string[]
) => Promise<void>;

type PermitsFetchProps = {
	page: number;
	sort: ISortOption;
	searchValue: string;
	themeIds: string[];
	lawIds: string[];
	productTypeIds: string[];
};

interface IPermitsOverviewProps {
	module: PermitsOverviewFieldsFragment;
}

const PermitsOverview = (props: IPermitsOverviewProps): React.JSX.Element => {
	const router = useRouter();
	const { formatMessage } = useIntl();
	const dispatch = useDispatch();

	const [query, setQuery] = useQueryParams({
		[PAGE_QUERY_KEY]: NumberParam,
		[SORT_QUERY_KEY]: NewOldSortParam,
		[SEARCH_QUERY_KEY]: StringParam,
		[THEME_QUERY_KEY]: ThemeArrayParam,
		[LAW_QUERY_KEY]: LawArrayParam,
		[PRODUCT_QUERY_KEY]: ProductArrayParam
	});

	const overviewRef = useRef<HTMLDivElement>(null);
	const { permits, permitsCount, modal, permitLaws, permitProductTypes } = useAppSelector((state) => state.permits);
	const { themes } = useAppSelector((state) => state.themes);

	const [modalFilters, setModalFilters] = useState<CheckboxItem[]>([]);
	const [modalOptions, setModalOptions] = useState<any>();

	const [requests, setRequests] = useState<PermitAlgoliaFragment[]>(permits || []);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(false);
	const [requestCount, setRequestCount] = useState<number>(permitsCount);

	const [sortIsOpen, setSortIsOpen] = useState<boolean>(false);
	const [filtersIsOpen, setFiltersIsOpen] = useState<boolean>(false);
	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { currentPage, setCurrentPage, Pagination } = usePagination(PERMIT_OVERVIEW_LIMIT);
	const { sort, setSort, Sort } = useSort('permits', 'alfabetical');
	const { searchValue, setSearchValue } = useSearch();
	const { selectedThemes, setSelectedThemesParam } = useTheme();
	const { selectedLaws, setSelectedLawsParam } = useLaw();
	const { selectedProductTypes, setSelectedProductTypesParam } = useProduct();

	const [search, setSearch] = useState<string | null>(searchValue);
	const debouncedSearch = useDebounce(search, 800);

	const windowSize = useWindowSize(100);
	const MOBILE_BREAKPOINT = 768;

	const fetchPermits: FetchPermits = async (
		page,
		newSort,
		newSearchValue,
		themeIds,
		lawIds,
		productType
	): Promise<void> => {
		setRequestIsLoading(true);

		try {
			const { results, total } = await searchPermits(
				newSearchValue || '',
				{
					themesIds: themeIds,
					permitLawsIds: lawIds,
					permitProductTypesIds: productType
				},
				{
					sort: newSort.id === 'desc' ? 'desc' : 'asc',
					page,
					limit: PERMIT_OVERVIEW_LIMIT,
					locale: router.locale
				}
			);

			setRequests(results);
			setRequestCount(total);
		} catch {
			setRequests([]);
			setRequestCount(0);
		}

		setRequestIsLoading(false);
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 140;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.scrollY - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handleDeleteFilter = (item?: IActiveFilter): void => {
		if (item?.label) {
			const newSelectedThemes = selectedThemes.filter((selection) => selection.label !== item.label);
			setSelectedThemesParam(newSelectedThemes.length > 0 ? newSelectedThemes : null);

			const newSelectedLaws = selectedLaws.filter((selection) => selection.label !== item.label);
			setSelectedLawsParam(newSelectedLaws.length > 0 ? newSelectedLaws : null);

			const newSelectedProductTypes = selectedProductTypes.filter((selection) => selection.label !== item.label);
			setSelectedProductTypesParam(newSelectedProductTypes.length > 0 ? newSelectedProductTypes : null);

			handleFetchSubsidy({
				page: 1,
				themeIds: newSelectedThemes.map((newItem) => String(newItem.id)),
				lawIds: newSelectedLaws.map((newItem) => String(newItem.id)),
				productTypeIds: newSelectedProductTypes.map((newItem) => String(newItem.id))
			});
		} else {
			setQuery({
				thema: null,
				product: null,
				wetgeving: null,
				pagina: null,
				sort: null,
				zoekterm: null
			});
			setSearch('');
			handleFetchSubsidy(undefined, true);
		}
	};

	const handleSortOnChange = (selectedSortFilter: { id: string; label: string } | undefined): void => {
		if (selectedSortFilter) {
			setSort(selectedSortFilter);
			handleFetchSubsidy({ page: 1, sort: selectedSortFilter });

			if (currentPage !== 1) {
				setCurrentPage(1);
			}
		}
	};

	const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setSearch(event.target.value);

		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			setCurrentPage(page);
			handleFetchSubsidy({ page });
		}
	};

	const handleFetchSubsidy = async (data?: Partial<PermitsFetchProps>, reset?: boolean): Promise<void> => {
		const selectedPage = reset ? 1 : data?.page || currentPage;
		const selectedSort = reset ? { id: '', label: '' } : data?.sort || sort;
		const selectedSearch = reset ? '' : data?.searchValue || debouncedSearch;

		const selectedThemeIds = reset ? [] : data?.themeIds || selectedThemes.map((theme) => String(theme.id));
		const selectedLawIds = reset ? [] : data?.lawIds || selectedLaws.map((law) => String(law.id));
		const selectedProductTypeIds = reset
			? []
			: data?.productTypeIds || selectedProductTypes.map((product) => String(product.id));

		scrollToTop();
		setHasFiltered(data !== undefined);
		fetchPermits(selectedPage, selectedSort, selectedSearch, selectedThemeIds, selectedLawIds, selectedProductTypeIds);
	};

	const permitFilters = usePermitsFilters(props, (themeIds: string[], lawIds: string[], productTypeIds: string[]) =>
		handleFetchSubsidy({ page: 1, themeIds, lawIds, productTypeIds })
	);

	// Close mobile dropdowns when resizing to desktop
	useEffect(() => {
		if ((windowSize.width || 0) >= MOBILE_BREAKPOINT) {
			setSortIsOpen(false);
			setFiltersIsOpen(false);
		}
	}, [windowSize.width]);

	useEffect(() => {
		handleFetchSubsidy(undefined);
	}, [initialQuery]);

	useEffect(() => {
		setSearchValue(debouncedSearch || null);

		if (debouncedSearch !== null) {
			handleFetchSubsidy({ page: 1 });
		}
	}, [debouncedSearch]);

	useEffect(() => {
		if (
			!hasFiltered &&
			!initialQuery &&
			(query[THEME_QUERY_KEY] ||
				query[LAW_QUERY_KEY] ||
				query[PRODUCT_QUERY_KEY] ||
				query[SEARCH_QUERY_KEY] ||
				query[SORT_QUERY_KEY])
		) {
			setInitialQuery(query);
		}
	}, [query]);

	useEffect(() => {
		if (!!query[SEARCH_QUERY_KEY] && initialQuery?.[SEARCH_QUERY_KEY] !== query[SEARCH_QUERY_KEY]) {
			setSearch(query[SEARCH_QUERY_KEY]);
		}
	}, [query[SEARCH_QUERY_KEY]]);

	const handleModalOpen = (): void => {
		dispatch(setModalOpen(!modal.modalOpen));
	};

	const setModalItems = (items: CheckboxItem[]): void => {
		switch (modal.modalType) {
			case 'themes':
				setSelectedThemesParam(items);
				break;

			case 'laws':
				setSelectedLawsParam(items);
				break;

			case 'productTypes':
				setSelectedProductTypesParam(items);
				break;

			default:
				break;
		}
	};
	useEffect(() => {
		const themeOptions = themes.map(({ documentId, attributes }) => ({
			id: documentId,
			label: attributes?.title || ''
		}));
		const lawsOptions = permitLaws.map(({ id, name }) => ({ id, label: name }));
		const productTypesOptions = permitProductTypes.map(({ id, name }) => ({ id, label: name }));

		switch (modal.modalType) {
			case 'themes':
				setModalFilters(selectedThemes);
				setModalOptions(themeOptions);
				break;

			case 'laws':
				setModalFilters(selectedLaws);
				setModalOptions(lawsOptions);
				break;

			case 'productTypes':
				setModalFilters(selectedProductTypes);
				setModalOptions(productTypesOptions);
				break;

			default:
				setModalFilters([]);
				setModalOptions(undefined);
		}
	}, [modal.modalOpen]);

	return (
		<>
			<CheckboxesModal
				modal={modal}
				filters={modalFilters || []}
				options={modalOptions || []}
				setModalOpen={handleModalOpen}
				setModalSelected={setModalItems}
			/>
			<div className={styles.header} ref={overviewRef}>
				<div className="container">
					<h2>{props.module.overviewTitleText}</h2>
					<span className={styles.searchContainer}>
						<Search
							searchValue={search || ''}
							searchPlaceholderText={props.module.searchPlaceholderText}
							handleSearchOnChange={handleSearchOnChange}
							small
						/>
					</span>
				</div>
			</div>

			<div className={styles.overview}>
				<div className="container">
					<div className={styles.overviewContainer}>
						<MobileActions
							filtersIsOpen={filtersIsOpen}
							setFiltersIsOpen={setFiltersIsOpen}
							sortIsOpen={sortIsOpen}
							setSortIsOpen={setSortIsOpen}
						/>

						<div className={styles.filter}>
							<FilterSidebar
								module={props.module}
								mobileIsOpen={filtersIsOpen}
								setMobileIsOpen={setFiltersIsOpen}
								filters={permitFilters}
								handleDeleteFilters={handleDeleteFilter}
								handleResetFilter={handleDeleteFilter}
								amountOfActiveFilters={[...selectedThemes, ...selectedLaws, ...selectedProductTypes].length > 0 ? 1 : 0}
								activeFilters={[...selectedThemes, ...selectedLaws, ...selectedProductTypes] as IActiveFilter[]}
							/>
						</div>
						<div className={styles.content}>
							<div className={styles.resultsHeader}>
								<h3 id={props.module.overviewTitleText}>{props.module.overviewTitleText}</h3>
								<div className={styles.sortBar}>
									<span id="pageResults" aria-live="polite">
										{formatMessage({ id: 'global.results' }, { results: requestCount })}
									</span>
									<Sort onChange={handleSortOnChange} mobileIsOpen={sortIsOpen} setMobileIsOpen={setSortIsOpen} />
								</div>
							</div>
							<div className={clsx(styles.permits, requestIsLoading && styles.permitsLoading)}>
								{requests.length > 0 ? (
									requests?.map((s, index) => <Card key={`permits-${index}`} title={s?.title || ''} slug={s.slug} />)
								) : (
									<EmptyState onClearFilters={handleDeleteFilter} isLoading={requestIsLoading} />
								)}
							</div>
						</div>
						<div className={styles.pagination}>
							<Pagination totalCount={requestCount} onPageChange={handlePageChange} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default PermitsOverview;
